import ButtonDesigned from '@UI-Elements/Buttons/ButtonDesigned';
import { Tooltip as MuiTooltip } from '@mui/material';
import {
  formatToPriceInDollars,
  FormatToPriceTemplate,
  FormatToShortPriceTemplate,
  formatToShortString,
} from '@utils/FormatValue';
import React from 'react';
import { trendDirectionType } from '../../sp-types/InventoryType';
import { IRiskPercent } from '../../sp-types/accountTypes';

import { LinearGauge, LinearGaugeProps } from '@UI-Elements/Charts/LinearGauge/LinearGauge';
import { getExposureColor, getExposureStatus } from '@utils/RiskUtils';
import { IMaterialExposure } from '../../sp-types/netExposureType';
import { LoadingLogoComponent } from '../Quotes/LoadingLogoComponent';
import { getExposureCalculation } from './dashboard-components/ExposeCalculation';
import { Material } from '../../sp-types';
import { ExposureBreakdown } from '../../utils/exposureCalculation';
import { getCurrencySymbol } from '../../hooks/useCurrencyRates';
import { InfoOutlined } from '@mui/icons-material';
import { useCategoryTerms } from '../../utils/categoryTerms';
import { CurveMiniChart } from '../HistoricalPrices/components/CurveMiniChart';
import { getHistoryPricesSince } from '../../sp-firebase/historyPricesFB';
import { getDateBefore } from '../../utils/gridUtils';
import { getCurveForIndex, PricePoint } from '../HistoricalPrices/components/HistoricalUtils';
import { CircularProgress } from '@mui/material';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import { ToggleChips } from './dashboard-components/ToggleChips';
import { MULTIPLE_MATS, NO_DATA } from './dashboardUtils';
import { PriceCurveCard } from './PriceCurveCard';
import { GaussBellCurve } from './dashboard-components/GaussBellCurve';

interface ExposureSingleCardProps {
  trendDirection?: trendDirectionType;
  mainTitle: string;
  secondaryTitle: string;
  amount: string | React.ReactNode;
  amountColor?: string;
  descriptionTitle?: string;
  toolTipText: string | any;
  ttPlace?: string | any;
}

const ExposureSingleCard: React.FC<ExposureSingleCardProps> = ({
  amount,
  mainTitle,
  secondaryTitle,
  toolTipText,
  ttPlace,
  amountColor,
  descriptionTitle,
  trendDirection,
}) => {
  const arrowDirectionDictionary: { [key: string]: string } = {
    appreciation: '/assets/icons/colored/green-triangle.svg',
    depreciation: '/assets/icons/colored/red-triangle.svg',
    flat: '',
  };

  return (
    <div>
      <div className="flex items-center gap-1.5 pb-2">
        <h4 className="text-lg font-semibold whitespace-nowrap">{mainTitle}</h4>
        {toolTipText && (
          <MuiTooltip title={toolTipText} arrow placement={ttPlace || 'top'}>
            <span className="cursor-help" style={{ fontSize: '14px', lineHeight: 1, marginBottom: '4px' }}>💡</span>
          </MuiTooltip>
        )}
      </div>
      <h6 className="text-xs text-gray-450">{secondaryTitle}</h6>
      <div className="flex  flex-row space-x-[0.75rem]">
        <p style={{ color: amountColor || 'black' }} className="text-2xl m-0">
          {amount}
        </p>
        {trendDirection && (
          <div className="flex flex-col justify-center ">
            <img className="w-[0.75rem] h-[0.75rem]  " src={arrowDirectionDictionary[trendDirection]} alt="" />
          </div>
        )}
      </div>
      {descriptionTitle && <p>{descriptionTitle}</p>}
    </div>
  );
};

interface ExposureLinerGaugeCardProps {
  linearGaugeProps: LinearGaugeProps; // Props for LinearGauge component
}

export const ExposureLinerGaugeCard: React.FC<ExposureLinerGaugeCardProps> = ({ linearGaugeProps }) => {
  return (
    <div className="grow">
      <LinearGauge {...linearGaugeProps} />
    </div>
  );
};

export interface IExposureCardProps {
  gaugeBarProps: LinearGaugeProps;
  netExposure: number;
  totalAmounts: IMaterialExposure;
  materialPriceStatus?: trendDirectionType;
  riskPercent?: IRiskPercent;
  onBalanceNowClickAction?: () => void;
  isBalanceNowLoading?: boolean;
  isExposureLoading?: boolean;
  mainTitle?: string;
  balanceButton?: 'center' | 'right';
  showExposureDetails?: boolean;
  onExpDetailClk?: () => void;
  material?: Material;
  isMiniView?: boolean;
  exposureBreakdown?: ExposureBreakdown; // Full breakdown for transparency
  isSpecificMode?: boolean; // True for specific formula, false for general
  selectedMaterial?: string; // Formula ID currently selected
  horizonMonths?: number;
  outOfScopeCounts?: { demand: number; supply: number; hedges: number };
}

export const ExposureCard: React.FC<IExposureCardProps> = ({
  gaugeBarProps,
  netExposure,
  totalAmounts,
  materialPriceStatus,
  riskPercent,
  onBalanceNowClickAction,
  isBalanceNowLoading = false,
  isExposureLoading = false,
  mainTitle = 'Exposure',
  balanceButton = 'center',
  showExposureDetails = false,
  onExpDetailClk,
  material,
  isMiniView = false,
  exposureBreakdown,
  isSpecificMode = false,
  selectedMaterial,
  horizonMonths,
  outOfScopeCounts,
}) => {
  const terms = useCategoryTerms();
  // Removed useCurrencyRates - now using useFormulasPrices in calculation hook
  const [selectedPercentile, setSelectedPercentile] = React.useState<number>(5); // Default to 5% risk
  const [futurePrices, setFuturePrices] = React.useState<PricePoint[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  // Calculate loss potential: VaR = |Exposure| × (Z×σ + μ)
  // μ = mean simple annual return (positive = prices rose = bad for buyer)
  const calculateLossPotential = (exposure: number, volatility: number, percentile: number, mean?: number): number => {
    const zScores: Record<number, number> = {
      5: 1.645,   // 5% chance of losing this much or more
      10: 1.282,
      25: 0.674,
    };
    
    const zScore = zScores[percentile] || 1.645;
    const mu = mean ?? 0;
    // VaR = |Exposure| × (Z × σ + μ)
    // Previous hedger-perspective formula (kept for reference):
    // return Math.abs(exposure) * Math.max(0, zScore * volatility - mu);
    return Math.abs(exposure) * Math.max(0, zScore * volatility + mu);
  };

  const exposureColor = getExposureColor(getExposureStatus(gaugeBarProps.riskTolerance, netExposure));

  React.useEffect(() => {
    const fetchFuturePrices = async () => {
      setLoading(true);
      const fromDate = getDateBefore(10);

      const prices = await getHistoryPricesSince(material?.id.replace('c1', '').replace('C1', '') || '', fromDate);
      if (prices?.length) {
        const curveData = getCurveForIndex(prices.length - 1, new Date(), prices);

        setFuturePrices(curveData);
      } else {
        setFuturePrices([]);
      }
      setLoading(false);
    };

    if (material?.id) {
      fetchFuturePrices();
    }
  }, [material?.id]);

  const descriptionTitle =
    netExposure !== 0
      ? exposureBreakdown?.netExposure?.quantity != null
        ? `${formatToShortString(Math.abs(exposureBreakdown.netExposure.quantity))} ${
            material?.unit ? (getUnitObj(material.unit)?.shortForm || 'MT') : 'MT'
          }`
        : MULTIPLE_MATS
      : NO_DATA;

  const cardProps1: ExposureSingleCardProps = {
    amount: FormatToShortPriceTemplate(netExposure),
    descriptionTitle,
    mainTitle,
    secondaryTitle: 'Net exposure',
    toolTipText: getExposureCalculation({
      netExposure,
      totalAmounts,
      exposureColor,
    }),
    ttPlace: 'right',
    amountColor: getExposureColor(getExposureStatus(gaugeBarProps.riskTolerance, netExposure)),
    trendDirection: materialPriceStatus,
  };

  const exposureValue = FormatToShortPriceTemplate(Math.abs((netExposure * (selectedPercentile || 1)) / 100));

  return (
    <div className="flex flex-1 gap-3 overflow-auto">
      <div className="flex flex-col shadow-md bg-white p-3 flex-1">
        {/* Title row - full width at top with inline breakdown - fixed min-height to prevent jumping */}
        <div className="flex items-start gap-3 pb-2 border-b border-gray-200 flex-wrap min-h-[50px]">
          <div className="flex items-baseline gap-2">
            <h4 className="text-lg font-semibold whitespace-nowrap">{mainTitle}</h4>
            <MuiTooltip
              arrow
              placement="bottom-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'inherit',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    maxWidth: 420,
                    p: 1.5,
                    '& .MuiTooltip-arrow': { color: 'white', '&::before': { border: '1px solid #e5e7eb' } },
                  },
                },
              }}
              title={
                <div>
                  {isSpecificMode && exposureBreakdown ? (
                    <div style={{ fontSize: '12px', lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#374151' }}>Exposure Calculation</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{formatToShortString(exposureBreakdown.demand.quantity)} MT</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>{terms.demand}</span>
                        <span style={{ color: '#9ca3af' }}>&minus;</span>
                        <span style={{ fontWeight: 600 }}>{formatToShortString(exposureBreakdown.supply.quantity)} MT</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>{terms.supply}</span>
                        <span style={{ color: '#9ca3af' }}>&minus;</span>
                        <span style={{ fontWeight: 600 }}>{formatToShortString(exposureBreakdown.hedges.quantity)} MT</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>{terms.hedge}</span>
                        <span style={{ color: '#9ca3af' }}>=</span>
                        <span style={{ fontWeight: 700 }}>{formatToShortString(exposureBreakdown.netExposure.quantity)} MT</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>Net Qty</span>
                        <span style={{ color: '#9ca3af' }}>&times;</span>
                        <span style={{ fontWeight: 600, color: '#2563eb' }}>
                          {getCurrencySymbol(exposureBreakdown.netExposure.currency)} {formatToShortString(exposureBreakdown.netExposure.spotPrice)}
                        </span>
                        <span style={{ color: '#2563eb', fontSize: 10 }}>Spot</span>
                        {exposureBreakdown.netExposure.fxRateToBase !== 1.0 && (
                          <>
                            <span style={{ color: '#9ca3af' }}>&times;</span>
                            <span style={{ fontWeight: 600, color: '#7c3aed' }}>{exposureBreakdown.netExposure.fxRateToBase.toFixed(4)}</span>
                            <span style={{ color: '#7c3aed', fontSize: 10 }}>FX</span>
                          </>
                        )}
                        <span style={{ color: '#9ca3af' }}>=</span>
                        <span style={{ fontWeight: 700, color: exposureColor }}>{FormatToShortPriceTemplate(exposureBreakdown.netExposure.valueBase)}</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#374151' }}>Exposure Calculation</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{FormatToShortPriceTemplate(totalAmounts.futureOrder)}</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>{terms.demand}</span>
                        <span style={{ color: '#9ca3af' }}>&minus;</span>
                        <span style={{ fontWeight: 600 }}>{FormatToShortPriceTemplate(totalAmounts.inventorySum)}</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>{terms.supply}</span>
                        <span style={{ color: '#9ca3af' }}>&minus;</span>
                        <span style={{ fontWeight: 600 }}>{FormatToShortPriceTemplate(totalAmounts.financialPositions)}</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>{terms.hedge}</span>
                        <span style={{ color: '#9ca3af' }}>=</span>
                        <span style={{ fontWeight: 700, color: exposureColor }}>{FormatToShortPriceTemplate(netExposure)}</span>
                      </div>
                    </div>
                  )}
                  {horizonMonths && outOfScopeCounts && (outOfScopeCounts.demand > 0 || outOfScopeCounts.supply > 0 || outOfScopeCounts.hedges > 0) && (
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: 6, borderTop: '1px solid #e5e7eb', paddingTop: 4 }}>
                      Showing {horizonMonths}-month horizon.
                      {' '}{outOfScopeCounts.demand + outOfScopeCounts.supply + outOfScopeCounts.hedges} positions excluded
                      ({outOfScopeCounts.demand > 0 ? `${outOfScopeCounts.demand} ${terms.demand.toLowerCase()}` : ''}
                      {outOfScopeCounts.demand > 0 && (outOfScopeCounts.supply > 0 || outOfScopeCounts.hedges > 0) ? ', ' : ''}
                      {outOfScopeCounts.supply > 0 ? `${outOfScopeCounts.supply} ${terms.supply.toLowerCase()}` : ''}
                      {outOfScopeCounts.supply > 0 && outOfScopeCounts.hedges > 0 ? ', ' : ''}
                      {outOfScopeCounts.hedges > 0 ? `${outOfScopeCounts.hedges} ${terms.hedge.toLowerCase()}` : ''})
                    </div>
                  )}
                </div>
              }
            >
              <InfoOutlined sx={{ fontSize: 16, color: '#9ca3af', cursor: 'help', '&:hover': { color: '#6b7280' } }} />
            </MuiTooltip>
          </div>
          
        </div>
        
        {/* Content row - gauge and stats */}
        <div className="flex flex-1 pt-2 gap-2">
          {/* Left part: gauge full-width + labels below */}
          <div className="relative flex flex-col flex-1 min-w-[20rem] gap-2 px-2 justify-end">
            <div className="mt-4">
              <ExposureLinerGaugeCard linearGaugeProps={gaugeBarProps} />
            </div>

            {/* Value + Quantity labels below gauge */}
            <div className="flex items-center justify-center gap-8 flex-1">
              {/* In specific mode: show quantity first */}
              {isSpecificMode && cardProps1.descriptionTitle && cardProps1.descriptionTitle !== 'No data' && cardProps1.descriptionTitle !== 'Multiple materials' && (
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-gray-500">Quantity</span>
                  <span className="text-2xl font-semibold" style={{ color: exposureColor }}>
                    {cardProps1.descriptionTitle}
                  </span>
                </div>
              )}
              <div className="flex items-baseline gap-2">
                <span className="text-sm text-gray-500">Net exposure</span>
                <span className="text-2xl font-bold" style={{ color: exposureColor }}>
                  {cardProps1.amount}
                </span>
              </div>
            </div>

            {balanceButton === 'center' && showExposureDetails && (
              <div className="w-full flex flex-row-reverse justify-between px-3">
                <ButtonDesigned type="button" style="outline" onButtonClick={() => onExpDetailClk?.()}>
                  Detailed exposure
                </ButtonDesigned>
              </div>
            )}

            {isExposureLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white opacity-75 z-50">
                <LoadingLogoComponent />
              </div>
            )}
          </div>

          {/* 3. Volatility */}
          {!isMiniView && (
            <div className="flex flex-col border-l-2 w-32 border-l-gray-200 pl-4 gap-1">
              <h4 className="text-lg whitespace-nowrap">Volatility risk</h4>
              <div className="flex flex-col gap-0.5">
                <div className="text-lg font-semibold">
                  {(material?.volatility || riskPercent?.volatility)
                    ? `${((material?.volatility ?? riskPercent?.volatility ?? 0) * 100).toFixed(1)}%`
                    : '0%'}
                </div>
                <MuiTooltip
                  arrow
                  placement="bottom"
                  title={
                    isSpecificMode ? (
                      <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
                        <b>Annual volatility (σ)</b> measures how much this material&apos;s price
                        fluctuates over a year, based on ~5 years of daily price data.
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
                        <b>Overall volatility</b> is the exposure-weighted average across
                        all your materials — materials with larger exposure contribute more.
                      </div>
                    )
                  }
                >
                  <h6 className="text-xs text-gray-450 cursor-help flex items-center gap-0.5">
                    Annual volatility
                    <InfoOutlined sx={{ fontSize: 13, color: '#9ca3af', '&:hover': { color: '#6b7280' } }} />
                  </h6>
                </MuiTooltip>
              </div>
            </div>
          )}

          {/* 3b. Bell curve */}
          {!isMiniView && (material?.volatility || riskPercent?.volatility) ? (
            <div className="flex flex-col border-l-2 border-l-gray-200 pl-3 justify-center">
              <GaussBellCurve
                sigma={material?.volatility ?? riskPercent?.volatility ?? 0}
                mu={material?.volatilityMean ?? riskPercent?.volatilityMean ?? 0}
                exposure={netExposure}
                percentile={selectedPercentile}
              />
            </div>
          ) : null}

          {/* 4. Loss */}
          {!isMiniView && (
            <div className="flex flex-col w-48 px-4 gap-1">
              <h4 className="text-lg whitespace-nowrap">Loss potential</h4>
              <div className="flex flex-col gap-0.5">
                <div className="text-lg font-semibold">
                  {(material?.volatility || riskPercent?.volatility) && netExposure
                    ? FormatToShortPriceTemplate(
                        calculateLossPotential(
                          netExposure,
                          material?.volatility ?? riskPercent?.volatility ?? 0,
                          selectedPercentile,
                          material?.volatilityMean ?? riskPercent?.volatilityMean,
                        )
                      )
                    : exposureValue || '$0'}
                </div>
                <MuiTooltip
                  arrow
                  placement="bottom"
                  title={
                    isSpecificMode ? (
                      <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
                        <b>Loss potential</b> uses Value at Risk (VaR): at the selected
                        confidence level, there is that probability of losing the shown
                        amount or more over one year, given your current exposure.
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
                        <b>Loss potential</b> is the combined worst-case estimate across
                        all materials at the selected confidence level. This is a conservative
                        figure that assumes no diversification benefit.
                      </div>
                    )
                  }
                >
                  <h6 className="text-xs text-gray-450 cursor-help flex items-center gap-0.5">
                    Loss value
                    <InfoOutlined sx={{ fontSize: 13, color: '#9ca3af', '&:hover': { color: '#6b7280' } }} />
                  </h6>
                </MuiTooltip>
              </div>
              <div className="flex mt-1">
                <ToggleChips
                  selected={selectedPercentile}
                  setSelected={setSelectedPercentile}
                  chips={[
                    { key: '5%', value: 5 },
                    { key: '10%', value: 10 },
                    { key: '25%', value: 25 },
                  ]}
                />
              </div>
            </div>
          )}

          {/* 5. Price per mt (value only) */}
          {!isMiniView && (
            <div className="flex flex-col w-36 border-l-2 border-l-gray-200 px-4 min-w-0">
              <PriceCurveCard
                mode="valueOnly"
                formulaCode={selectedMaterial && selectedMaterial !== 'general' ? selectedMaterial : null}
                formulaName={material?.name}
                material={material}
              />
            </div>
          )}

          {/* 6. Trend curve (dedicated panel) */}
          {!isMiniView && (
            <div className="flex flex-col w-40 px-2 min-w-0">
              <PriceCurveCard
                mode="curveOnly"
                formulaCode={selectedMaterial && selectedMaterial !== 'general' ? selectedMaterial : null}
                formulaName={material?.name}
                material={material}
              />
            </div>
          )}
          {['hide', 'right'].includes(balanceButton) && (
            <div className="flex flex-col justify-center ms-20 p-3 w-56">
              {balanceButton === 'right' && onBalanceNowClickAction && (
                <ButtonDesigned
                  type="button"
                  style="primary"
                  disableButton={isBalanceNowLoading}
                  onButtonClick={onBalanceNowClickAction}
                  isFunctionLoading={isBalanceNowLoading}
                >
                  Balance now
                </ButtonDesigned>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
